import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { useGame } from "@/context/GameContext";
import { SKILL_LABEL, skillIcon } from "@/lib/gameData";
import {
  Swords, Heart, Pickaxe, Hand, Wind, Hammer,
  Shield, Leaf, Fish, ChefHat, Flame,
  Crosshair, Wand2, Sparkles, Target, TreePine, Sprout,
  Skull, Home, VenetianMask, Anvil, PawPrint,
} from "lucide-react";

/**
 * Skills panel — OSRS-style compact 3-column grid of skill tiles.
 * Clicking a tile opens the dedicated per-skill page.
 */

// Column-major OSRS classic order. Each entry: [skill_id, LucideFallback]
const GRID = [
  // Row-by-row layout (rendered directly).
  ["attack",       Swords],       ["hitpoints",     Heart],       ["mining",     Pickaxe],
  ["strength",     Hand],         ["agility",       Wind],        ["smithing",   Hammer],
  ["defence",      Shield],       ["herblore",      Leaf],        ["fishing",    Fish],
  ["ranged",       Crosshair],    ["thieving",      VenetianMask],["cooking",    ChefHat],
  ["prayer",       Sparkles],     ["crafting",      Anvil],       ["firemaking", Flame],
  ["magic",        Wand2],        ["fletching",     Target],      ["woodcutting",TreePine],
  ["runecrafting", Sparkles],     ["slayer",        Skull],       ["farming",    Sprout],
  ["construction", Home],         ["hunter",        PawPrint],    ["__total",    null],
];

// Combat level formula (OSRS-ish, simplified for our engine).
function combatLevel(skills) {
  const g = (k) => skills?.[k]?.level ?? 1;
  const base = 0.25 * (g("defence") + g("hitpoints") + Math.floor(g("prayer") / 2));
  const melee = 0.325 * (g("attack") + g("strength"));
  const range = 0.325 * (Math.floor(3 * g("ranged") / 2));
  const magic = 0.325 * (Math.floor(3 * g("magic") / 2));
  return Math.floor(base + Math.max(melee, range, magic));
}

function SkillTile({ skill, Fallback, onClick }) {
  const src = skillIcon(skill);
  const label = SKILL_LABEL[skill];
  return (
    <button
      onClick={onClick}
      data-testid={`ov-skill-${skill}`}
      title={label}
      className="group relative flex items-center border border-stone-800 bg-[color:var(--bg-panel-inset,#0f0d0b)] hover:border-amber-700/70 hover:bg-stone-900 transition-colors duration-150 h-14 lg:h-16"
      style={{
        boxShadow: "inset 0 0 0 1px rgba(20, 18, 15, 0.6), inset 0 0 24px rgba(0,0,0,0.35)",
      }}
    >
      {/* Icon */}
      <span className="flex-shrink-0 w-1/2 h-full flex items-center justify-center">
        {src ? (
          <img
            src={src}
            alt={label}
            className="w-8 h-8 lg:w-10 lg:h-10 object-contain drop-shadow-[0_1px_0_rgba(0,0,0,0.9)]"
          />
        ) : Fallback ? (
          <Fallback size={22} className="text-amber-300/90" strokeWidth={1.6} />
        ) : null}
      </span>

      {/* Numbers half with diagonal slash */}
      <span className="relative flex-1 h-full">
        <NumSlash />
        <span
          className="absolute top-0.5 right-1.5 font-numeric text-[13px] lg:text-sm text-amber-300 leading-none"
          data-testid={`ov-skill-${skill}-cur`}
        >
          <TileLevel skill={skill} />
        </span>
        <span
          className="absolute bottom-0.5 left-1.5 font-numeric text-[13px] lg:text-sm text-amber-300/95 leading-none"
        >
          <TileLevel skill={skill} />
        </span>
      </span>
    </button>
  );
}

function TileLevel({ skill }) {
  const { snap } = useGame();
  const lv = snap?.skills?.[skill]?.level ?? 1;
  return <>{lv}</>;
}

function NumSlash() {
  // Diagonal slash from top-right to bottom-left inside the numeric half.
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      <line x1="90" y1="12" x2="10" y2="88" stroke="rgba(120, 92, 40, 0.55)" strokeWidth="2" />
    </svg>
  );
}

function TotalTile({ total, combat }) {
  return (
    <div
      data-testid="ov-total-tile"
      className="border border-amber-800/70 bg-[color:var(--bg-panel-inset,#0f0d0b)] h-14 lg:h-16 flex flex-col items-center justify-center px-2"
      style={{
        boxShadow: "inset 0 0 0 1px rgba(140, 100, 40, 0.15), inset 0 0 22px rgba(0,0,0,0.4)",
      }}
    >
      <span className="font-body text-[10px] uppercase tracking-widest text-amber-500 leading-none">
        Total level
      </span>
      <span className="font-numeric text-lg lg:text-xl text-amber-300 leading-none mt-1" data-testid="ov-total-level">
        {total}
      </span>
      <span className="font-numeric text-[9px] text-stone-500 mt-0.5">
        Combat · <span className="text-amber-400">{combat}</span>
      </span>
    </div>
  );
}

export function SkillsPanel() {
  const navigate = useNavigate();
  const { snap } = useGame();

  const totalLevel = useMemo(() => {
    if (!snap?.skills) return 0;
    return Object.values(snap.skills).reduce((s, v) => s + (v?.level ?? 0), 0);
  }, [snap?.skills]);

  const combat = useMemo(() => combatLevel(snap?.skills || {}), [snap?.skills]);

  const openSkill = (skill) => {
    navigate(`/play?panel=skill&skill=${skill}`);
  };

  return (
    <div className="p-4 lg:p-6" data-testid="skills-panel">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <button
          className="text-amber-400 border-b-2 border-amber-500 pb-1 px-1 text-xs font-body"
          data-testid="tab-skills"
        >
          Skills
        </button>
        <button className="text-stone-500 hover:text-amber-500 pb-1 px-1 text-xs font-body" title="Coming soon">
          Mastery
        </button>
        <div className="flex-1" />
        <span className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
          Tap a skill to open its page
        </span>
      </div>

      {/* Panel frame */}
      <div
        className="p-2 lg:p-3 rounded-md max-w-md mx-auto lg:max-w-lg border border-stone-700"
        style={{
          background:
            "linear-gradient(180deg, #3d3628 0%, #2c2820 60%, #1e1b16 100%)",
          boxShadow: "inset 0 0 0 1px rgba(60, 50, 28, 0.8), 0 4px 0 rgba(0,0,0,0.35)",
        }}
      >
        <div className="grid grid-cols-3 gap-1" data-testid="skills-overview-grid">
          {GRID.map(([id, Fallback], i) => {
            if (id === "__total") {
              return <TotalTile key={i} total={totalLevel} combat={combat} />;
            }
            return (
              <SkillTile
                key={id}
                skill={id}
                Fallback={Fallback}
                onClick={() => openSkill(id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
